import { useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import errorMessage from "../lib/errors";
import { CheckCircle2, Search, Ruler, Truck, Store, Wrench, Mail, MessageSquare } from "lucide-react";

/** Customer self-service order lookup — email + order number, no account needed. */
export default function OrderLookupPage() {
  const [email, setEmail] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setOrder(null);
    setLoading(true);
    try {
      const r = await api.post("/orders/lookup", {
        email: email.trim(),
        order_number: orderNumber.trim().toUpperCase(),
      });
      setOrder(r.data);
    } catch (err) {
      setError(errorMessage(err, "We couldn't find that order. Double-check your email and order number."));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setOrder(null);
    setError("");
  };

  const up = order?.upsells || {};
  const emailStatus = order?.notifications?.email;
  const smsStatus = order?.notifications?.sms;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#f7faf6" }}>
      <header className="px-5 py-4 border-b bg-white sticky top-0 z-20">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <Link to="/" data-testid="lookup-logo-link" className="flex items-center gap-2">
            <img
              src="https://customer-assets.emergentagent.com/job_tech-dispatch-31/artifacts/a6ta9f48_SCREENZZ-LOGO-RGB_TextOnly.png"
              alt="ScreenzZ"
              className="h-8"
            />
          </Link>
          <span className="step-pill">Track order</span>
        </div>
      </header>

      <main className="flex-1 px-5 py-6">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-6">
            <Search className="w-10 h-10 brand-text mx-auto" />
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-3 text-[var(--brand-text)]">
              Find your order
            </h1>
            <p className="muted-text mt-2">
              Enter your email and the order number we sent you.
            </p>
          </div>

          {!order && (
            <form onSubmit={submit} className="option-card" data-testid="lookup-form">
              <label className="block text-xs uppercase muted-text mb-1 font-semibold">Email</label>
              <input
                data-testid="lookup-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="w-full rounded-full border-2 border-[var(--brand-border)] bg-white px-4 py-2.5 text-sm mb-3 focus:outline-none focus:border-[var(--brand-green)]"
              />
              <label className="block text-xs uppercase muted-text mb-1 font-semibold">Order number</label>
              <input
                data-testid="lookup-order-number"
                type="text"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                placeholder="SZ-260501-ABC123"
                required
                className="w-full rounded-full border-2 border-[var(--brand-border)] bg-white px-4 py-2.5 text-sm uppercase focus:outline-none focus:border-[var(--brand-green)]"
              />

              {error && (
                <p data-testid="lookup-error" className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
                  {error}
                </p>
              )}

              <button
                data-testid="lookup-submit"
                type="submit"
                disabled={loading}
                className="w-full mt-4 cta-btn disabled:opacity-50"
              >
                {loading ? "Looking up…" : "Find my order"}
              </button>
            </form>
          )}

          {order && (
            <div data-testid="lookup-result">
              <div className="option-card">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-5 h-5 brand-text" />
                  <p className="font-bold text-[var(--brand-text)]">{order.order_number}</p>
                </div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="muted-text">Status</span>
                  <span
                    data-testid="lookup-status"
                    className="font-semibold capitalize"
                  >
                    {order.status.replace(/_/g, " ")}
                  </span>
                </div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="muted-text">{order.product_name}</span>
                  <span>×{order.quantity}</span>
                </div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="muted-text">Placed</span>
                  <span>{new Date(order.created_at).toLocaleDateString()}</span>
                </div>
                <div className="border-t border-[var(--brand-border)] my-2" />
                <div className="flex justify-between text-lg font-bold text-[var(--brand-text)]">
                  <span>Total</span>
                  <span data-testid="lookup-total">${order.pricing.total.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xs muted-text mt-1">
                  <span>Payment</span>
                  <span className="capitalize font-semibold">{order.payment_status}</span>
                </div>
              </div>

              {(emailStatus || smsStatus) && (
                <div className="flex gap-2 mt-3">
                  {emailStatus && (
                    <div
                      className="flex-1 rounded-2xl p-3 border"
                      style={{
                        background: emailStatus.sent === false && !emailStatus.stubbed ? "#fef2f2" : "#f0fdf4",
                        borderColor: emailStatus.sent === false && !emailStatus.stubbed ? "#fecaca" : "#bbf7d0",
                      }}
                    >
                      <Mail className="w-4 h-4 brand-text mb-1" />
                      <p className="text-xs font-semibold">
                        {emailStatus.sent === false && !emailStatus.stubbed
                          ? "Email failed"
                          : emailStatus.stubbed
                          ? "Email queued"
                          : "Email sent"}
                      </p>
                    </div>
                  )}
                  {smsStatus && (
                    <div
                      className="flex-1 rounded-2xl p-3 border"
                      style={{
                        background: smsStatus.sent === false && !smsStatus.stubbed ? "#fef2f2" : "#f0fdf4",
                        borderColor: smsStatus.sent === false && !smsStatus.stubbed ? "#fecaca" : "#bbf7d0",
                      }}
                    >
                      <MessageSquare className="w-4 h-4 brand-text mb-1" />
                      <p className="text-xs font-semibold">
                        {smsStatus.sent === false && !smsStatus.stubbed
                          ? "Text failed"
                          : smsStatus.stubbed
                          ? "Text queued"
                          : "Text sent"}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Fulfillment */}
              {(up.pickup || up.delivery || up.delivery_install) && (
                <div className="option-card mt-3">
                  {up.pickup && (
                    <>
                      <Store className="w-5 h-5 brand-text mb-1" />
                      <p className="font-semibold text-[var(--brand-text)]">Pickup at the shop</p>
                      <p className="text-xs muted-text mt-1">We'll text you when it's ready.</p>
                    </>
                  )}
                  {up.delivery && (
                    <>
                      <Truck className="w-5 h-5 brand-text mb-1" />
                      <p className="font-semibold text-[var(--brand-text)]">Delivery scheduled</p>
                    </>
                  )}
                  {up.delivery_install && (
                    <>
                      <Wrench className="w-5 h-5 brand-text mb-1" />
                      <p className="font-semibold text-[var(--brand-text)]">Delivery + Install scheduled</p>
                    </>
                  )}
                </div>
              )}

              {up.pro_measurement && (
                <div className="option-card mt-3">
                  <Ruler className="w-5 h-5 brand-text mb-1" />
                  <p className="font-semibold text-[var(--brand-text)]">Pro Measurement on the way</p>
                  <p className="text-xs muted-text mt-1">
                    {order.pro_measurement_appointment_at
                      ? "Your appointment is locked in — we'll see you then."
                      : "We'll text you within 1 business day to lock in a time."}
                  </p>
                </div>
              )}

              {order.measurements_required && !order.measurements_completed && (
                <div
                  className="rounded-2xl p-3 mt-3 border"
                  style={{ background: "#fffbeb", borderColor: "#fde68a" }}
                >
                  <p className="text-xs font-semibold text-amber-900">
                    We still need your measurements — check your email/SMS for the link.
                  </p>
                </div>
              )}

              <button
                data-testid="lookup-reset"
                type="button"
                onClick={reset}
                className="w-full mt-4 py-3 rounded-full border-2 border-[var(--brand-border)] font-semibold"
              >
                Look up another order
              </button>
            </div>
          )}
        </div>
      </main>

      <footer className="py-6 text-center text-xs muted-text">
        <p>© {new Date().getFullYear()} ScreenzZ · DIY Ordering</p>
        <div className="flex items-center justify-center gap-4 mt-1 flex-wrap">
          <Link to="/" data-testid="lookup-footer-home-link"
            className="text-[11px] muted-text hover:brand-text underline-offset-2 hover:underline">
            Start new order
          </Link>
          <Link to="/privacy-policy"
            className="text-[11px] muted-text hover:brand-text underline-offset-2 hover:underline">
            Privacy
          </Link>
          <Link to="/terms"
            className="text-[11px] muted-text hover:brand-text underline-offset-2 hover:underline">
            Terms
          </Link>
          <a href="tel:8774000889"
            className="text-[11px] muted-text hover:brand-text underline-offset-2 hover:underline">
            Contact
          </a>
          <Link to="/admin/login" data-testid="lookup-footer-admin-link"
            className="text-[11px] muted-text hover:brand-text underline-offset-2 hover:underline">
            Admin
          </Link>
        </div>
      </footer>
    </div>
  );
}
